import type { FunnelPageItem, FunnelPageRouting, FunnelRouteTarget, FunnelRoutingEvent } from '../types';
import { DRAFT_FUNNEL_PAGE_ID } from '../constants/funnels.constants';

export function getRouteForEvent(page: FunnelPageItem, event: FunnelRoutingEvent): FunnelRouteTarget | null {
    return page.routing?.[event] ?? null;
}

function getBranchTargetIds(pages: FunnelPageItem[]): Set<number> {
    const targets = new Set<number>();
    for (const page of pages) {
        const routing = page.routing;
        if (!routing) continue;
        if (routing.on_purchase) targets.add(routing.on_purchase.page_id);
        if (routing.on_skip) targets.add(routing.on_skip.page_id);
    }
    return targets;
}

export function getLinearNextPage(pages: FunnelPageItem[], anchorOrder: number): FunnelPageItem | null {
    const branchTargets = getBranchTargetIds(pages);
    return (
        pages
            .filter(p => p.order > anchorOrder && p.page_id !== DRAFT_FUNNEL_PAGE_ID && !branchTargets.has(p.page_id))
            .sort((a, b) => a.order - b.order)[0] ?? null
    );
}

export interface ResolveNextPageResult {
    page: FunnelPageItem | null;
    anchorOrder: number | null;
}

export function resolveNextPage(
    pages: FunnelPageItem[],
    currentPageId: number,
    event: FunnelRoutingEvent,
    anchorOrder: number | null = null
): ResolveNextPageResult {
    const current = pages.find(p => p.page_id === currentPageId);
    if (!current) return { page: null, anchorOrder };

    const baseAnchor = typeof anchorOrder === 'number' ? anchorOrder : current.order;

    const route = getRouteForEvent(current, event);
    if (route) {
        const target = pages.find(p => p.page_id === route.page_id);
        if (target) return { page: target, anchorOrder: baseAnchor };
    }

    const next = getLinearNextPage(pages, baseAnchor);
    if (!next) return { page: null, anchorOrder: baseAnchor };
    return { page: next, anchorOrder: next.order };
}

export interface BrokenRoute {
    fromPageId: number;
    event: FunnelRoutingEvent;
    missingTargetId: number;
}

/** Routes whose target no longer exists in the funnel. */
export function getBrokenRoutes(pages: FunnelPageItem[]): BrokenRoute[] {
    const pageIds = new Set(pages.map(p => p.page_id));
    const out: BrokenRoute[] = [];
    for (const page of pages) {
        if (!page.routing) continue;
        (['on_purchase', 'on_skip'] as FunnelRoutingEvent[]).forEach(event => {
            const target = page.routing?.[event];
            if (target && !pageIds.has(target.page_id)) {
                out.push({ fromPageId: page.page_id, event, missingTargetId: target.page_id });
            }
        });
    }
    return out;
}

/**
 * Strip any routing entry whose target no longer exists in `pages`. Used
 * pre-save to keep stale references from surviving a save after a delete.
 */
export function cleanRouting(
    routing: FunnelPageRouting | null | undefined,
    pages: FunnelPageItem[]
): FunnelPageRouting | null {
    if (!routing) return null;
    const valid = new Set(pages.map(p => p.page_id));
    const keep = (r: FunnelRouteTarget | null) => (r && valid.has(r.page_id) ? r : null);
    const next: FunnelPageRouting = {
        on_purchase: keep(routing.on_purchase),
        on_skip: keep(routing.on_skip),
    };
    return next.on_purchase || next.on_skip ? next : null;
}
